/**
 * SkillResultCard — Renders skill results visually in chat.
 * - image → zoomable image
 * - audio → vidstack audio player
 * - video → vidstack video player
 * - text  → formatted card with copy button
 * - data  → JSON/table display
 *
 * Rule 94: Modüler — tek sorumluluk.
 * CSS: var(--os-*) only. Icons: lucide-react only.
 */
import { Check, Copy, Download, FileText, Image, Music, Video } from 'lucide-react'
import { useState } from 'react'
import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'

export interface SkillResultCardProps {
  skillName: string
  resultType: 'image' | 'audio' | 'video' | 'text' | 'data'
  result: string // URL for media, text/JSON for text/data
  query?: string
}

const TYPE_ICONS = {
  image: Image,
  audio: Music,
  video: Video,
  text: FileText,
  data: FileText,
}

const TYPE_COLORS: Record<string, string> = {
  image: '#a855f7',
  audio: '#10b981',
  video: '#3b82f6',
  text: '#ec4899',
  data: '#f59e0b',
}

export function SkillResultCard({ skillName, resultType, result, query }: SkillResultCardProps) {
  const [copied, setCopied] = useState(false)
  const Icon = TYPE_ICONS[resultType] ?? FileText
  const accentColor = TYPE_COLORS[resultType] ?? 'var(--os-accent)'

  const handleCopy = () => {
    navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = result
    a.download = `${skillName}-result`
    a.click()
  }

  return (
    <div style={{
      alignSelf: 'flex-start',
      maxWidth: '95%',
      background: 'var(--os-card)',
      border: '1px solid var(--os-border)',
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 10,
      overflow: 'hidden',
      fontSize: 11,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 10px',
        borderBottom: '1px solid var(--os-border)',
        background: 'var(--os-surface)',
      }}>
        <Icon size={12} style={{ color: accentColor, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, color: 'var(--os-text)', flex: 1, fontSize: 11 }}>
          {skillName}
        </span>
        {query && (
          <span style={{
            fontSize: 9.5, color: 'var(--os-muted)',
            maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {query}
          </span>
        )}
        <span style={{
          fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
          background: `${accentColor}18`, color: accentColor,
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          {resultType}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: resultType === 'image' ? 0 : '8px 10px' }}>
        {resultType === 'image' && <ImageResult url={result} skillName={skillName} onDownload={handleDownload} />}
        {resultType === 'audio' && <AudioResult url={result} />}
        {resultType === 'video' && <VideoResult url={result} />}
        {resultType === 'text' && <TextResult text={result} copied={copied} onCopy={handleCopy} />}
        {resultType === 'data' && <DataResult data={result} copied={copied} onCopy={handleCopy} />}
      </div>
    </div>
  )
}

/* ── Image ── */
function ImageResult({ url, skillName, onDownload }: { url: string; skillName: string; onDownload: () => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <Zoom>
        <img
          src={url}
          alt={skillName}
          style={{
            width: '100%', maxWidth: 320, display: 'block',
            borderRadius: '0 0 8px 8px',
          }}
          loading="lazy"
        />
      </Zoom>
      <button
        onClick={onDownload}
        title="Download image"
        style={{
          position: 'absolute', bottom: 8, right: 8,
          background: 'rgba(0,0,0,0.6)', border: 'none',
          borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
          color: 'var(--os-text)', display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 10, backdropFilter: 'blur(4px)',
        }}
      >
        <Download size={11} /> Save
      </button>
    </div>
  )
}

/* ── Audio ── */
function AudioResult({ url }: { url: string }) {
  return (
    <div style={{ padding: '4px 0' }}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        src={url}
        controls
        style={{ width: '100%', accentColor: 'var(--os-accent)', borderRadius: 6 }}
      />
    </div>
  )
}

/* ── Video ── */
function VideoResult({ url }: { url: string }) {
  return (
    <div style={{ borderRadius: 6, overflow: 'hidden', background: '#000' }}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        src={url}
        controls
        style={{ width: '100%', maxHeight: 240, display: 'block', objectFit: 'contain' }}
        playsInline
      />
    </div>
  )
}

/* ── Text ── */
function TextResult({ text, copied, onCopy }: { text: string; copied: boolean; onCopy: () => void }) {
  const preview = text.length > 400 ? text.slice(0, 400) + '…' : text
  return (
    <div>
      <pre style={{
        margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: 'var(--os-text)', fontSize: 11, lineHeight: 1.6,
        maxHeight: 200, overflowY: 'auto',
        fontFamily: 'inherit',
      }}>
        {preview}
      </pre>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button onClick={onCopy} style={{
          background: 'none', border: '1px solid var(--os-border)',
          borderRadius: 5, padding: '3px 8px', cursor: 'pointer',
          color: copied ? '#10b981' : 'var(--os-muted)',
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontFamily: 'inherit',
        }}>
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

/* ── Data ── */
function DataResult({ data, copied, onCopy }: { data: string; copied: boolean; onCopy: () => void }) {
  let parsed: unknown = null
  let isJson = false
  try { parsed = JSON.parse(data); isJson = true } catch { /* not JSON */ }

  return (
    <div>
      <pre style={{
        margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: 'var(--os-muted)', fontSize: 10, lineHeight: 1.5,
        maxHeight: 180, overflowY: 'auto',
        fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
        background: 'var(--os-surface)', padding: '6px 8px', borderRadius: 6,
      }}>
        {isJson ? JSON.stringify(parsed, null, 2) : data}
      </pre>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button onClick={onCopy} style={{
          background: 'none', border: '1px solid var(--os-border)',
          borderRadius: 5, padding: '3px 8px', cursor: 'pointer',
          color: copied ? '#10b981' : 'var(--os-muted)',
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontFamily: 'inherit',
        }}>
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}
